import { Button, Card, SvgIcon } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { RiAddLine } from '@remixicon/react';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomDataGrid from '../../../components/CustomDataGrid';
import { useAppDataContext } from '../../../contexts/appData';
import { useLanguage } from '../../../contexts/language';
import { Island } from '../../../types/island';
import AdminTableDetailLink from '../../../components/AdminTableDetailLink';
import { useDataGridSearchParamsFiltersManager } from '../../../hooks/useSearchParamsFilters';
import IslandVisibilityBadge from './IslandVisibilityBadge';

const IslandsPage = () => {
  const { t, language } = useLanguage();
  const { islands: appIslands, isFetching } = useAppDataContext();

  const { initialFilterModel, onFilterModelChange } =
    useDataGridSearchParamsFiltersManager();

  const islands = useMemo(() => {
    const sortedIslands = appIslands.map(island => {
      const sortedPorts = island.ports.filter(port => port.isActive);
      sortedPorts.sort((a, b) =>
        a.names[language].localeCompare(b.names[language]),
      );
      return { ...island, ports: sortedPorts };
    });

    sortedIslands.sort((a, b) => {
      return a.isMainIsland
        ? -1
        : a.names[language].localeCompare(b.names[language]);
    });

    return sortedIslands;
  }, [appIslands, language]);

  const columns = useMemo<GridColDef<Island>[]>(
    () => [
      {
        field: 'names',
        flex: 1,
        headerName: t('content.island-name'),
        minWidth: 200,
        cellClassName: params =>
          params.row.isMainIsland ? 'font-bold italic' : '',
        valueGetter: names => names[language],
      },
      {
        field: 'ports',
        flex: 3,
        minWidth: 200,
        headerName: t('content.island-ports'),
        valueGetter: ports =>
          (ports as Island[]).map(port => port.names[language]).join(', '),
      },
      {
        field: 'isPublicVisible',
        flex: 1,
        display: 'flex',
        minWidth: 140,
        headerName: t('content.status'),
        type: 'singleSelect',
        valueOptions: [
          { value: true, label: t('content.visible') },
          { value: false, label: t('content.hidden') },
        ],
        renderCell: params => (
          <IslandVisibilityBadge isVisible={params.value as boolean} small />
        ),
      },
      {
        field: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        display: 'flex',
        renderCell: params => (
          <AdminTableDetailLink
            isLoading={isFetching}
            href={`/admin/islands/${params.id}`}
          />
        ),
      },
    ],
    [isFetching, language, t],
  );
  return (
    <MainSectionTemplate
      section={t('content.services')}
      title={t('content.islands')}
      actions={
        <Link to="/admin/islands/create">
          <Button
            variant="contained"
            startIcon={<SvgIcon component={RiAddLine} />}
            size="small"
          >
            {t('content.add-new')}
          </Button>
        </Link>
      }
    >
      <Card variant="outlined">
        <CustomDataGrid
          rows={islands}
          loading={isFetching}
          columns={columns}
          onFilterModelChange={onFilterModelChange}
          initialState={{
            filter: {
              filterModel: initialFilterModel,
            },
            sorting: {
              sortModel: [
                {
                  field: 'names',
                  sort: 'asc',
                },
              ],
            },
          }}
        />
      </Card>
    </MainSectionTemplate>
  );
};

export default IslandsPage;
